import type { PlanType } from '@/lib/plans'
import { PLAN_HIERARCHY } from '@/lib/plans'
import { useContextStore } from '@/stores/context'
import { useManagedAccountStore } from '@/stores/managed-account'

/**
 * Hook to check if the current user has access to a feature based on their plan
 * @param minimumPlan - The minimum plan required to access the feature
 * @param options - `useManagedAccount: true` gates on the managed account while
 *   managing one. Set it for account-administration features under /settings that
 *   the backend scopes via the managed-account header (webhooks, users,
 *   white-label, sustainability). By default the check uses the primary
 *   (logged-in) account, matching where most operations run (envelopes, sandbox,
 *   api-tokens) — the backend keeps those on the logged user even while managing.
 * @returns boolean - true if user has access, false otherwise
 *
 * Plan hierarchy: Free < One < Pro < Max
 */
export function useHasFeatureAccess(
  minimumPlan: PlanType,
  options?: { useManagedAccount?: boolean },
): boolean {
  const accountContext = useContextStore((state) => state.userAccountContext)
  const { isManaging, featureType: managedFeatureType } =
    useManagedAccountStore()

  const userPlan =
    options?.useManagedAccount && isManaging
      ? managedFeatureType
      : accountContext?.balanceInformation.featureType

  const userLevel = PLAN_HIERARCHY[userPlan || 'Free'] || 0
  const requiredLevel = PLAN_HIERARCHY[minimumPlan]

  return userLevel >= requiredLevel
}
